import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './ColoringGame.css'

const PAPER = '#fffdf8'
const INK = '#1c1917'

const PENCIL_SIZES = [
  { id: 'small', label: 'Small pencil', width: 10, dot: 8 },
  { id: 'medium', label: 'Medium pencil', width: 20, dot: 14 },
  { id: 'large', label: 'Large pencil', width: 36, dot: 22 },
]

const COLORS = [
  { id: 'red', label: 'Red', hex: '#FF5A5F' },
  { id: 'orange', label: 'Orange', hex: '#FF8A3D' },
  { id: 'yellow', label: 'Yellow', hex: '#FFD23F' },
  { id: 'green', label: 'Green', hex: '#63D146' },
  { id: 'mint', label: 'Mint', hex: '#2EC9A0' },
  { id: 'blue', label: 'Blue', hex: '#4C8DFF' },
  { id: 'purple', label: 'Purple', hex: '#8B6CFF' },
  { id: 'pink', label: 'Pink', hex: '#FF6BB5' },
  { id: 'tan', label: 'Tan', hex: '#E0A15A' },
  { id: 'brown', label: 'Brown', hex: '#8D5A3C' },
  { id: 'black', label: 'Black', hex: '#2B2B2B' },
]

/** Closed areas, back to front. A tap fills only the shape under the pointer. */
const REGIONS = [
  { id: 'ear-left', label: 'Left ear', cx: 102, cy: 98, rx: 52, ry: 52, sw: 7 },
  { id: 'ear-right', label: 'Right ear', cx: 298, cy: 98, rx: 52, ry: 52, sw: 7 },
  { id: 'ear-left-inner', label: 'Left inner ear', cx: 102, cy: 102, rx: 26, ry: 26, sw: 5 },
  { id: 'ear-right-inner', label: 'Right inner ear', cx: 298, cy: 102, rx: 26, ry: 26, sw: 5 },
  { id: 'arm-left', label: 'Left arm', cx: 74, cy: 348, rx: 38, ry: 66, rotate: -24, sw: 7 },
  { id: 'arm-right', label: 'Right arm', cx: 326, cy: 348, rx: 38, ry: 66, rotate: 24, sw: 7 },
  { id: 'foot-left', label: 'Left foot', cx: 150, cy: 456, rx: 52, ry: 36, sw: 7 },
  { id: 'foot-right', label: 'Right foot', cx: 250, cy: 456, rx: 52, ry: 36, sw: 7 },
  { id: 'body', label: 'Body', cx: 200, cy: 352, rx: 112, ry: 108, sw: 7 },
  { id: 'belly', label: 'Belly', cx: 200, cy: 372, rx: 60, ry: 68, sw: 5.5 },
  { id: 'head', label: 'Head', cx: 200, cy: 198, rx: 108, ry: 96, sw: 7 },
  { id: 'eye-left', label: 'Left eye', cx: 158, cy: 176, rx: 16, ry: 20, sw: 5 },
  { id: 'eye-right', label: 'Right eye', cx: 242, cy: 176, rx: 16, ry: 20, sw: 5 },
  { id: 'snout', label: 'Snout', cx: 200, cy: 232, rx: 50, ry: 38, sw: 5.5 },
  { id: 'nose', label: 'Nose', cx: 200, cy: 216, rx: 18, ry: 13, sw: 5 },
]

function regionTransform(region) {
  if (!region.rotate) return undefined
  return `rotate(${region.rotate} ${region.cx} ${region.cy})`
}

function RegionStrokes({ region, strokes }) {
  const mine = strokes.filter((stroke) => stroke.regionId === region.id)
  if (mine.length === 0) return null
  return (
    <g clipPath={`url(#bear-clip-${region.id})`} pointerEvents="none">
      {mine.map((stroke) =>
        stroke.points.length < 2 ? (
          <circle
            key={stroke.id}
            cx={stroke.points[0].x}
            cy={stroke.points[0].y}
            r={stroke.size / 2}
            fill={stroke.color}
          />
        ) : (
          <polyline
            key={stroke.id}
            points={stroke.points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')}
            fill="none"
            stroke={stroke.color}
            strokeWidth={stroke.size}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ),
      )}
    </g>
  )
}

function BearShape({ region, fill, outline, onKeyDown }) {
  return (
    <ellipse
      cx={region.cx}
      cy={region.cy}
      rx={region.rx}
      ry={region.ry}
      transform={regionTransform(region)}
      fill={outline ? 'none' : fill}
      stroke={outline ? INK : 'none'}
      strokeWidth={outline ? region.sw : 0}
      strokeLinejoin="round"
      strokeLinecap="round"
      pointerEvents={outline ? 'none' : 'auto'}
      data-region={outline ? undefined : region.id}
      className={outline ? undefined : 'color-game__region'}
      role={outline ? undefined : 'button'}
      aria-label={outline ? undefined : region.label}
      tabIndex={outline ? undefined : 0}
      onKeyDown={outline ? undefined : onKeyDown}
    />
  )
}

function PencilIcon() {
  return (
    <svg className="color-game__tool-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4.2 19.8l4.2-1.1L18.6 8.5a2.2 2.2 0 0 0 0-3.1l-.9-.9a2.2 2.2 0 0 0-3.1 0L4.4 14.7 3.3 18.9a.8.8 0 0 0 .9.9z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M13.2 6.2l4.6 4.6" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

function FillIcon() {
  return (
    <svg className="color-game__tool-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 14.5 14.2 6l5 5.2-6.2 8.3H8v-5z" fill="currentColor" />
      <circle cx="17.2" cy="17.6" r="2" fill="currentColor" />
    </svg>
  )
}

export function ColoringGame({ onClose }) {
  const [color, setColor] = useState(COLORS[5].hex)
  const [fills, setFills] = useState({})
  const [strokes, setStrokes] = useState([])
  const [history, setHistory] = useState([])
  const [tool, setTool] = useState('pencil')
  const [pencilSize, setPencilSize] = useState(PENCIL_SIZES[1].width)
  const painting = useRef(false)
  const colorRef = useRef(color)
  const fillsRef = useRef(fills)
  const toolRef = useRef(tool)
  const sizeRef = useRef(pencilSize)
  const activeStroke = useRef(null)
  const strokeSerial = useRef(1)
  const svgRef = useRef(null)
  const closeRef = useRef(null)
  colorRef.current = color
  fillsRef.current = fills
  toolRef.current = tool
  sizeRef.current = pencilSize

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    function onKey(event) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  function regionAt(event) {
    const el = document.elementFromPoint(event.clientX, event.clientY)
    return el?.getAttribute?.('data-region') || null
  }

  function toSvgPoint(event) {
    const svg = svgRef.current
    if (!svg) return null
    const point = svg.createSVGPoint()
    point.x = event.clientX
    point.y = event.clientY
    const matrix = svg.getScreenCTM()
    if (!matrix) return null
    const local = point.matrixTransform(matrix.inverse())
    return { x: local.x, y: local.y }
  }

  function strokeColor(regionId) {
    const chosen = colorRef.current
    if (chosen !== PAPER) return chosen
    return fillsRef.current[regionId] || PAPER
  }

  function fillRegion(id) {
    const next = colorRef.current
    const prevColor = fillsRef.current[id] || PAPER
    if (prevColor === next) return
    const nextFills = { ...fillsRef.current }
    if (next === PAPER) delete nextFills[id]
    else nextFills[id] = next
    fillsRef.current = nextFills
    setFills(nextFills)
    setHistory((entries) => [...entries, { type: 'fill', regionId: id, prev: prevColor }])
  }

  function beginStroke(regionId, point) {
    const stroke = {
      id: strokeSerial.current,
      regionId,
      color: strokeColor(regionId),
      size: sizeRef.current,
      points: [point],
    }
    strokeSerial.current += 1
    activeStroke.current = stroke
    setStrokes((current) => [...current, stroke])
    setHistory((entries) => [...entries, { type: 'stroke', strokeId: stroke.id }])
  }

  function extendStroke(point) {
    const active = activeStroke.current
    if (!active) return
    const last = active.points[active.points.length - 1]
    const dx = point.x - last.x
    const dy = point.y - last.y
    if (dx * dx + dy * dy < 0.8) return
    active.points.push(point)
    const points = active.points.slice()
    setStrokes((current) =>
      current.map((stroke) => (stroke.id === active.id ? { ...stroke, points } : stroke)),
    )
  }

  function drawAt(event) {
    const regionId = regionAt(event)
    const point = toSvgPoint(event)
    if (!regionId || !point) {
      activeStroke.current = null
      return
    }
    const active = activeStroke.current
    if (!active || active.regionId !== regionId) beginStroke(regionId, point)
    else extendStroke(point)
  }

  function onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    painting.current = true
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // Some browsers only capture a real pointer. Drawing still follows the move events.
    }
    if (toolRef.current === 'fill') {
      const id = regionAt(event)
      if (id) fillRegion(id)
      return
    }
    drawAt(event)
  }

  function onPointerMove(event) {
    if (!painting.current) return
    if (toolRef.current === 'fill') {
      const id = regionAt(event)
      if (id) fillRegion(id)
      return
    }
    drawAt(event)
  }

  function onPointerUp() {
    painting.current = false
    activeStroke.current = null
  }

  function onRegionKeyDown(event) {
    if (toolRef.current !== 'fill') return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    const id = event.currentTarget.getAttribute('data-region')
    if (id) fillRegion(id)
  }

  function undo() {
    setHistory((entries) => {
      if (entries.length === 0) return entries
      const last = entries[entries.length - 1]
      if (last.type === 'stroke') {
        setStrokes((current) => current.filter((stroke) => stroke.id !== last.strokeId))
      } else {
        setFills((current) => {
          const next = { ...current }
          if (!last.prev || last.prev === PAPER) delete next[last.regionId]
          else next[last.regionId] = last.prev
          return next
        })
      }
      return entries.slice(0, -1)
    })
  }

  function startOver() {
    activeStroke.current = null
    painting.current = false
    setFills({})
    setStrokes([])
    setHistory([])
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="color-game" role="dialog" aria-modal="true" aria-labelledby="color-game-title">
      <div className="color-game__sheet">
        <header className="color-game__header">
          <div>
            <h2 id="color-game-title" className="color-game__title">Color the bear</h2>
            <p className="color-game__hint">
              {tool === 'pencil' ? 'Draw inside the lines.' : 'Tap a shape to fill it.'}
            </p>
          </div>
          <button ref={closeRef} type="button" className="color-game__close" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="color-game__stage">
          <div className="color-game__paper">
            <svg
              ref={svgRef}
              className={`color-game__art color-game__art--${tool}`}
              viewBox="0 0 400 510"
              aria-label="Bear coloring page"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <defs>
                {REGIONS.map((region) => (
                  <clipPath id={`bear-clip-${region.id}`} key={region.id}>
                    <ellipse
                      cx={region.cx}
                      cy={region.cy}
                      rx={region.rx}
                      ry={region.ry}
                      transform={regionTransform(region)}
                    />
                  </clipPath>
                ))}
              </defs>
              {REGIONS.map((region) => (
                <g key={region.id}>
                  <BearShape region={region} fill={fills[region.id] || PAPER} onKeyDown={onRegionKeyDown} />
                  <RegionStrokes region={region} strokes={strokes} />
                  <BearShape region={region} outline />
                </g>
              ))}
              <g pointerEvents="none">
                <circle cx="162" cy="180" r="7" fill={INK} />
                <circle cx="246" cy="180" r="7" fill={INK} />
                <circle cx="159" cy="176" r="2.4" fill="#fff" />
                <circle cx="243" cy="176" r="2.4" fill="#fff" />
                <path
                  d="M176 248 Q200 270 224 248"
                  fill="none"
                  stroke={INK}
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </g>
            </svg>
          </div>
        </div>

        <div className="color-game__dock">
          <div className="color-game__swatches" role="listbox" aria-label="Colors">
            {COLORS.map((swatch) => {
              const selected = swatch.hex === color
              return (
                <button
                  key={swatch.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-label={swatch.label}
                  className={`color-game__swatch${selected ? ' is-selected' : ''}`}
                  style={{ background: swatch.hex }}
                  onClick={() => setColor(swatch.hex)}
                />
              )
            })}
            <button
              type="button"
              role="option"
              aria-selected={color === PAPER}
              aria-label="Eraser"
              className={`color-game__swatch color-game__swatch--eraser${color === PAPER ? ' is-selected' : ''}`}
              onClick={() => setColor(PAPER)}
            />
          </div>
          <div className="color-game__tools" role="group" aria-label="Drawing tools">
            <button
              type="button"
              className={`color-game__tool${tool === 'pencil' ? ' is-selected' : ''}`}
              aria-pressed={tool === 'pencil'}
              onClick={() => setTool('pencil')}
            >
              <PencilIcon />
              Pencil
            </button>
            <button
              type="button"
              className={`color-game__tool${tool === 'fill' ? ' is-selected' : ''}`}
              aria-pressed={tool === 'fill'}
              onClick={() => setTool('fill')}
            >
              <FillIcon />
              Fill
            </button>
            <div className={`color-game__sizes${tool === 'pencil' ? '' : ' is-inactive'}`} role="group" aria-label="Pencil size">
              {PENCIL_SIZES.map((size) => {
                const selected = tool === 'pencil' && pencilSize === size.width
                return (
                  <button
                    key={size.id}
                    type="button"
                    className={`color-game__size${selected ? ' is-selected' : ''}`}
                    aria-label={size.label}
                    aria-pressed={selected}
                    onClick={() => {
                      setTool('pencil')
                      setPencilSize(size.width)
                    }}
                  >
                    <span
                      className="color-game__size-dot"
                      style={{
                        width: size.dot,
                        height: size.dot,
                        background: color === PAPER ? '#d6d3d1' : color,
                      }}
                    />
                  </button>
                )
              })}
            </div>
          </div>
          <div className="color-game__actions">
            <button type="button" className="color-game__reset" onClick={undo} disabled={history.length === 0}>
              Undo
            </button>
            <button type="button" className="color-game__reset" onClick={startOver}>
              Start over
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
